import React from 'react'
import className from 'classnames'
import moment from 'moment'
import isEmpty from 'lodash/isEmpty'

import TableHeader from './TableHeader'
import { useCheckIds } from '../hooks'
import { requestCallback, createDownloadableLink } from '../lib/helpers'
import { canManage } from '../lib/policies'
import { textModerationBan, textModerationDelete, textModerationIgnore, textModerationBulkUpdate } from '../lib/api'

const TextModerationLists = ({ tasks = [], total = 0 }) => {
  const {
    checkIds,
    renderCheckBox,
    renderCheckBoxForCheckAll,
  } = useCheckIds({
    data: tasks
  })
  const getStatusColor = (status) => {
    switch (status) {
      case 'uncheck':
        return 'orange'
      case 'ignored':
        return 'green'
      case 'delete':
      case 'ban':
        return 'red'
      default:
        return 'gray'
    }
  }
  const shouldDisabledBulkActions = checkIds.length === 0
  const requestTextModerationBan = (task) => requestCallback(textModerationBan(task))
  const requestTextModerationDelete = (task) => requestCallback(textModerationDelete(task))
  const requestTextModerationIgnore = (task) => requestCallback(textModerationIgnore(task))
  const requestTextModerationBulkUpdate = (action) => {
    if(isEmpty(checkIds)) return

    const updatedItems = tasks.filter(({ id }) => checkIds.indexOf(id) > -1)
    requestCallback(textModerationBulkUpdate(updatedItems, action))
  }

  return (
    <React.Fragment>
      <div className='MainHeader'>
        <div className='MainHeader__left'>
          <h2 className='MainHeader__h2'>Text AI Task</h2>
          <p className='MainHeader__left__p'>Total {total.toLocaleString()} tasks</p>
        </div>
      </div>

      <div className='TableChecker'>
        {renderCheckBoxForCheckAll()}
        <div className='TableChecker__right'>
          <a
            className='TableChecker__right__a TableChecker__right__a--eye-close'
            disabled={shouldDisabledBulkActions}
            onClick={() => requestTextModerationBulkUpdate('ignore')}
          />
          <a
            className='TableChecker__right__a TableChecker__right__a--no-entry'
            disabled={shouldDisabledBulkActions}
            onClick={() => requestTextModerationBulkUpdate('ban')}
          />
          <a
            className='TableChecker__right__a TableChecker__right__a--trash'
            disabled={shouldDisabledBulkActions}
            onClick={() => requestTextModerationBulkUpdate('delete')}
          />
          {
            canManage && (
              <React.Fragment>
                <a
                  className='TableChecker__right__text-link TableChecker__right__text-link--pre-separator'
                  href={createDownloadableLink(`/${gon.currentProvider}/api/text_moderations/download_json`)}
                >
                  JSON
                </a>
                <a
                  className='TableChecker__right__text-link TableChecker__right__text-link--pre-separator'
                  href={createDownloadableLink(`/${gon.currentProvider}/api/text_moderations/download_csv`)}
                >
                  CSV
                </a>
              </React.Fragment>
            )
          }
        </div>
      </div>

      <div className='Table Table--text-ai-report-list'>
        {(() => {
          const columnHeaders = [
            {
              label: '',
            },
            {
              label: 'ID',
            },
            {
              label: 'Post ID',
            },
            {
              label: 'Text',
            },
            {
              label: 'Review Score',
              sortKey: 'review_score',
            },
            {
              label: 'Score'
            },
            {
              label: 'Status',
            },
            {
              label: 'Created at',
            },
            {
              label: 'Updated at',
              sortKey: 'updated_at',
            },
            {
              label: 'Action',
            },
          ]

          return (
            <div className='Table__row'>
              {columnHeaders.map((header, key) => (
                <TableHeader
                  key={key}
                  header={header}
                />
              ))}
            </div>
          )
        })()}

        {tasks.map((task) => (
          <div
            className={className('Table__row', {
              'Table__row--active': checkIds.includes(task.id),
            })}
            key={task.id}
            data-x={task}
          >
            <div className='Table__col'>
              {renderCheckBox(task)}
            </div>

            <div className='Table__col'>
              {task.id}
            </div>

            <div className='Table__col'>
              <a
                className='Table__a'
                href={`/${gon.currentProvider}/posts/${task.postId}`}
              >
                {task.postId}
              </a>
            </div>
            <div className='Table__col'>{task.text}</div>
            <div className='Table__col'>{task.reviewScore}</div>
            <div className='Table__col'>{task.score}</div>
            <div className="Table__col">
              <span className={`Badge Badge--${getStatusColor(task.status)}`}>
                {task.status}
              </span>
            </div>
            <div
              className='Table__col'
              dangerouslySetInnerHTML={
                {
                  __html: task.createdAt !== null ?
                    moment(task.createdAt).format('YYYY-MM-DD<br />HH:mm')
                    :
                    'N/A'
                }
              }
            />
            <div
              className='Table__col'
              dangerouslySetInnerHTML={
                {
                  __html: task.updatedAt !== null ?
                    moment(task.updatedAt).format('YYYY-MM-DD<br />HH:mm')
                    :
                    'N/A'
                }
              }
            />

            <div className='Table__col'>
              <div className='Table__controls'>
                <a className='Table__controls__a Table__controls__a--eye-close' onClick={() => requestTextModerationIgnore(task)} />
                <a className='Table__controls__a Table__controls__a--no-entry' onClick={() => requestTextModerationBan(task)} />
                <a className='Table__controls__a Table__controls__a--trash' onClick={() => requestTextModerationDelete(task)} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </React.Fragment>
  )
}

export default TextModerationLists
